/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../../../lib/@waca/upgrades/UpgradeBase',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'./ContainerPageIdUpgradeHelper',
	'underscore'
], (UpgradeBase, UniqueId, ContainerPageIdUpgradeHelper, _) => {

	const ABSOLUTE_TYPE = 'absolute';
	const SCALINGABSOLUTE_TYPE = 'scalingAbsolute';
	const CONTAINER_TYPE = 'container';
	const WIDGET_TYPE = 'widget';
	const GROUP_TYPE = 'group';
	const INFOGRAPHIC_TEMPLATE_TYPE = 'Infographic';
	const REGULAR_TEMPLATE_TYPE = 'Template';

	/**
	* Upgrades the absolute layout to be in a parent container similar to relative layouts for the page size funtionality.
	* Upgrades the layout to contain a pageSize based off widget positioning/page types.
	**/

	class LayoutPageSizeUpgrade extends UpgradeBase {

		constructor() {
			super();

			this.VERSION = 1011;
			this._containerPageIdUpgrader = new ContainerPageIdUpgradeHelper();
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			return Promise.resolve()
				.then(() => {
					this._init();

					if (spec && spec.layout) {
						this._upgradeLayout(spec.layout);
						this._upgradePageSize(spec.layout);

						this._containerPageIdUpgrader.upgrade(spec, this._containerPageIdMap);
					}
					return spec;
				});
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		/**
		 * Set the container id upgrade function
		 * If supplied, a custom container upgrade function will be called to upgrade the spec
		 * @param {object} containerPageIdUpgrade - container id upgrade object which implements the following interface:
		 * upgrade(spec, idMap)
		 * Currently only used for unit testing purposes
		 */
		setContainerIdUpgrader(containerPageIdUpgrade) {
			if (containerPageIdUpgrade) {
				this._containerPageIdUpgrader = containerPageIdUpgrade;
			}
		}

		_init() {
			this._hasRelativeLayouts = false;
			this._hasAbsoluteLayouts = false;
			this._relativeTemplateType = undefined;
			// Default minimum page size during upgrade
			this._boundingPageSize = { width: 1280, height: 720 };
			this._containerPageIdMap = {};
		}

		_upgradeLayout(layout) {
			if (layout.items) {
				// Recursively upgrade child layouts
				for (var item of layout.items) {
					this._upgradeLayout(item);
				}
			}
			this._determinePageSizeFromLayout(layout);
			this._determineRelativeTemplateType(layout);
			this._determineLayoutTypes(layout);
			this._upgradeAbsoluteContainer(layout);
		}

		_determinePageSizeFromLayout(layout) {
			if (layout.type === ABSOLUTE_TYPE) {
				// Determine bounds from widgets & groups
				for (var item of layout.items) {
					if (item.type === WIDGET_TYPE || item.type === GROUP_TYPE) {
						let rawStyle = this._getRawStyle(item.style, 'px');
						if (rawStyle) {
							let right = rawStyle.left + rawStyle.width;
							if (right > this._boundingPageSize.width) {
								this._boundingPageSize.width = right;
							}
							let bottom = rawStyle.top + rawStyle.height;
							if (bottom > this._boundingPageSize.height) {
								this._boundingPageSize.height = bottom;
							}
						}
					}
				}
			}
		}

		_determineRelativeTemplateType(layout) {
			if (layout.type === CONTAINER_TYPE) {
				let templateName = layout.templateName;
				if (templateName) {
					if (templateName.lastIndexOf('Infographics', 0) === 0) {
						this._relativeTemplateType = INFOGRAPHIC_TEMPLATE_TYPE;
					} else if (templateName.lastIndexOf('Template', 0) === 0) {
						this._relativeTemplateType = REGULAR_TEMPLATE_TYPE;
					}
				}
			}
		}

		_determineLayoutTypes(layout) {
			if (layout.type === ABSOLUTE_TYPE) {
				this._hasAbsoluteLayouts = true;
			} else if (layout.type === SCALINGABSOLUTE_TYPE) {
				this._hasRelativeLayouts = true;
			}
		}

		// Absolute layout will now be under a parent container, similar to relative layouts
		_upgradeAbsoluteContainer(layout) {
			if (layout.type === ABSOLUTE_TYPE) {
				let copyLayout = _.deepClone(layout);
				// Start clean on new container
				for (var prop in layout) {
					delete layout[prop];
				}
				layout.id = UniqueId.get('model');
				layout.items = [ copyLayout ];
				layout.title = copyLayout.title;
				layout.type = CONTAINER_TYPE;
				layout.templateName = copyLayout.templateName;
				delete copyLayout.title;
				delete copyLayout.templateName;

				this._containerPageIdMap[copyLayout.id] = layout.id;
			}
		}

		_getRawStyle(style, units) {
			// Validate style units (can have bad dashboards with a mix of relative/absolute coordinates)
			if (style.left.indexOf(units) !== -1 && style.top.indexOf(units) !== -1 &&
					style.width.indexOf(units) !== -1 && style.height.indexOf(units) !== -1) {
				return {
					left: parseInt(style.left, 10),
					top: parseInt(style.top, 10),
					width: parseInt(style.width, 10),
					height: parseInt(style.height, 10)
				};
			}
			return undefined;
		}

		_upgradePageSize(layout) {
			if (this._hasAbsoluteLayouts && !this._hasRelativeLayouts) {
				layout.pageSize = this._boundingPageSize;
			} else if (this._hasRelativeLayouts && !this._hasAbsoluteLayouts) {
				layout.pageSize = this._getDefaultPageSizeForTemplate();
			} else if (this._hasRelativeLayouts && this._hasAbsoluteLayouts) {
				// A combination of absolute and relative
				// 1.) Calculate minimum bounding rect of absolute items
				// 2a.) Increase height to match relative aspect ratio (template or infographics)
				// 2b.) or if height needs to be smaller, then increase width to match relative aspect ratio.
				layout.pageSize = this._boundingPageSize;
				let absoluteRatio = this._boundingPageSize.height/this._boundingPageSize.width;
				let relativePageSize = this._getDefaultPageSizeForTemplate();
				let relativeRatio = relativePageSize.height/relativePageSize.width;

				if (absoluteRatio < relativeRatio) {
					// Adjust the height to match the aspect ratio
					layout.pageSize = {
						width: this._boundingPageSize.width,
						height: Math.round(this._boundingPageSize.width * relativeRatio)
					};
				} else {
					// Adjust the width to match the aspect ratio
					layout.pageSize = {
						width: Math.round(this._boundingPageSize.height / relativeRatio),
						height: this._boundingPageSize.height
					};
				}
			}
		}

		_getDefaultPageSizeForTemplate() {
			// Page aspect ratio for height: (1:0.55 tabbed, 1:2 infographic)
			if (this._relativeTemplateType === INFOGRAPHIC_TEMPLATE_TYPE) {
				return { width: 512, height: 1024 };
			} else {
				return { width: 1280, height: 704 };
			}
		}

	}
	return new LayoutPageSizeUpgrade();
});
